import { Dtos } from "@inrev/common";
import { Option } from "@inrev/core";
import { type Factor, type ProCon } from "@portal/api/decisioning";
import {
	useCreateDecisioningFactor,
	useCreateDecisioningFactorGroup,
	useCreateProCon,
	useFetchDecisioningFactorGroups,
	useFetchDecisioningFactors,
	useFetchObservedValues,
	useUpdateProCon,
} from "@portal/api/decisioning/admin";
import { useMemo, useRef, useState } from "react";
import { isApiError } from "../../../../../utils/error";
import {
	SearchableDropdownInput,
	type SearchableDropdownInputOption,
} from "../../../../form/inputs/SearchableDropdownInput";
import { TextAreaInput } from "../../../../form/inputs/TextAreaInput";
import { cn } from "../../../../lib/utils";
import { PanelSheet } from "../../../../patterns/panel-sheet";
import { SegmentedControls, Tag, Text } from "../../../../primitives";
import { PRO_CON_IMPORTANCE_LABELS } from "../../decisioningLabels";
import { CloseWhenMissing } from "../CloseWhenMissing";
import { useDecisioningData } from "../DecisioningDataContext";
import { type DecisioningSheet, useDecisioningPanel } from "../DecisioningPanelContext";
import { PanelField } from "../PanelField";
import { type FactorEditDraft } from "./factorEditDraft";
import { ImportancePicker } from "./ImportancePicker";
import {
	EMPTY_NEW_FACTOR,
	NEW_GROUP,
	NewFactorBox,
	type NewFactorDraft,
	resolveGroup,
} from "./NewFactorBox";
import { ObservedValueField, type ObservedValuePreview } from "./ObservedValueField";
import {
	type ObservedValueDraft,
	parseObservedValueDraft,
	toObservedValueDraft,
} from "./observedValue";

type Kind = Dtos.Decisioning.ProConKind;
type Importance = Dtos.Decisioning.ProConImportance;
type FactorSheetState = Extract<DecisioningSheet, { kind: "factor" }>;

const LENS_ORDER = Dtos.Decisioning.lensSchema.options;
const EMPTY_FACTORS: Factor[] = [];
const EMPTY_GROUPS: Factor["group"][] = [];

const sortFactors = (factors: readonly Factor[]): Factor[] =>
	[...factors].sort(
		(a, b) =>
			LENS_ORDER.indexOf(a.group.lens) - LENS_ORDER.indexOf(b.group.lens) ||
			a.group.sortOrder - b.group.sortOrder ||
			a.sortOrder - b.sortOrder,
	);

const groupLabel = (factor: Factor) => `${factor.group.lens} · ${factor.group.name}`;

const KIND_NOUN: Record<Kind, string> = { pro: "pros", con: "cons" };
const KIND_LABELS: Record<Kind, string> = { pro: "Pro", con: "Con" };
const SIDE_DOT_CLASSES: Record<Kind, string> = { pro: "bg-confirm", con: "bg-danger" };
const KINDS: readonly Kind[] = ["pro", "con"];

const isConflict = (error: unknown): boolean => isApiError(error) && error.statusCode === 409;

const groupCreateErrorMessage = (error: unknown, name: string, lens: string): string =>
	isConflict(error)
		? `A group named "${name}" already exists in ${lens}.`
		: "Couldn't add the group. Try again.";

const factorCreateErrorMessage = (error: unknown, name: string, groupName: string): string =>
	isConflict(error)
		? `A factor named "${name}" already exists in ${groupName}.`
		: "Couldn't create the factor. Try again.";

const normalizeName = (name: string) => name.trim().toLowerCase();

/** The existing factor whose name matches the search text, ignoring case and outer spaces. */
const findFactorNamed = (factors: readonly Factor[], query: string): Factor | undefined => {
	const wanted = normalizeName(query);
	return wanted === ""
		? undefined
		: factors.find((factor) => normalizeName(factor.name) === wanted);
};

const NO_PREVIEW: ObservedValuePreview = { status: "none" };
const LOADING_PREVIEW: ObservedValuePreview = { status: "loading" };
const UNKNOWN_PREVIEW: ObservedValuePreview = { status: "unknown" };

const previewOf = (value: Option<Dtos.Decisioning.FactorValue>): ObservedValuePreview =>
	Option.isSome(value) ? { status: "value", value } : NO_PREVIEW;

/** Adds a pro or con, or edits an existing one's importance and rationale. */
export const FactorSheet = ({ sheet }: { sheet: FactorSheetState }) => {
	const { proConsById } = useDecisioningData();
	if (sheet.mode === "add") return <FactorSheetForm sheet={sheet} />;
	const proCon = proConsById.get(sheet.proConId);
	return proCon ? <FactorSheetForm sheet={sheet} proCon={proCon} /> : <CloseWhenMissing />;
};

const ReadOnlyFactor = ({ proCon }: { proCon: ProCon }) => (
	<div
		className="flex flex-col gap-1 rounded-lg bg-surface-light p-3"
		data-testid="factor-sheet-readonly"
	>
		<div className="flex items-center gap-2">
			<Tag color={proCon.kind === "pro" ? "green" : "red"}>
				{proCon.kind === "pro" ? "Pro" : "Con"}
			</Tag>
			<Text type="body-strong" color="primary">
				{proCon.factor.name}
			</Text>
		</div>
		<Text type="caption" color="tertiary" className="font-normal">
			{groupLabel(proCon.factor)}
		</Text>
	</div>
);

const FactorSheetForm = ({ sheet, proCon }: { sheet: FactorSheetState; proCon?: ProCon }) => {
	const { accountId, closeSheet, jumpTo, openSheet, setTab, toastOnSave } = useDecisioningPanel();
	const { factors = EMPTY_FACTORS } = useFetchDecisioningFactors();
	const { factorGroups = EMPTY_GROUPS } = useFetchDecisioningFactorGroups();
	const { createProConAsync } = useCreateProCon(accountId, {
		onCreated: (created) => jumpTo({ tab: "factors", id: created.id }),
	});
	/** What the sheet held when Save was clicked; the sheet has closed by the time a save fails. */
	const savedDraft = useRef<FactorEditDraft | null>(null);
	const { updateProConAsync } = useUpdateProCon(accountId, {
		failureAction: ({ id }) => {
			const draft = savedDraft.current;
			if (!draft) return undefined;
			return {
				label: "Reopen",
				onClick: () => openSheet({ kind: "factor", mode: "edit", proConId: id, draft }),
			};
		},
	});
	const { createFactorAsync } = useCreateDecisioningFactor();
	const { createFactorGroupAsync } = useCreateDecisioningFactorGroup();
	const hadOverride = proCon?.observedValueSource === "underwriter";
	const { observedValues, observedValuesError } = useFetchObservedValues(
		accountId,
		proCon === undefined || hadOverride,
	);

	const editing = proCon !== undefined;
	const [kind, setKind] = useState<Kind>(
		sheet.mode === "add" ? sheet.side : (proCon?.kind ?? "pro"),
	);
	const [factorId, setFactorId] = useState("");
	const [creating, setCreating] = useState(false);
	const [newFactor, setNewFactor] = useState<NewFactorDraft>(EMPTY_NEW_FACTOR);
	const restored = sheet.mode === "edit" ? sheet.draft : undefined;
	const [importance, setImportance] = useState<Importance>(
		sheet.mode === "edit"
			? (restored?.importance ?? sheet.importance ?? proCon?.importance ?? 1)
			: (sheet.importance ?? 1),
	);
	const [detail, setDetail] = useState(restored?.detail ?? proCon?.detail ?? "");
	const [overriding, setOverriding] = useState(restored?.overriding ?? hadOverride);
	const [valueDraft, setValueDraft] = useState<ObservedValueDraft>(
		restored
			? restored.valueDraft
			: hadOverride
				? toObservedValueDraft(proCon.observedValue)
				: null,
	);
	const [initial] = useState({ kind, importance, detail, overriding, valueDraft });
	const dirty =
		factorId !== "" ||
		creating ||
		kind !== initial.kind ||
		importance !== initial.importance ||
		detail !== initial.detail ||
		overriding !== initial.overriding ||
		valueDraft !== initial.valueDraft;
	const [submitting, setSubmitting] = useState(false);
	const [groupError, setGroupError] = useState<string>();
	const [submitError, setSubmitError] = useState<string>();

	const options = useMemo<SearchableDropdownInputOption[]>(
		() =>
			sortFactors(factors).map((factor) => ({
				value: factor.id,
				label: factor.name,
				group: groupLabel(factor),
				searchValues: [factor.name, factor.group.lens, factor.group.name],
			})),
		[factors],
	);
	const selectedFactor = factors.find((factor) => factor.id === factorId);
	const valueType = proCon?.factor.valueType ?? (creating ? "text" : selectedFactor?.valueType);
	const parsedValue = valueType ? parseObservedValueDraft(valueDraft, valueType) : undefined;
	const valueInvalid = overriding && parsedValue === undefined;
	const valueFactor = proCon?.factor ?? (creating ? undefined : selectedFactor);
	const preview = ((): ObservedValuePreview => {
		if (proCon && !hadOverride) return previewOf(proCon.observedValue);
		if (!valueFactor || Option.isNone(valueFactor.key)) return NO_PREVIEW;
		if (observedValuesError) return UNKNOWN_PREVIEW;
		if (!observedValues) return LOADING_PREVIEW;
		return previewOf(observedValues.get(valueFactor.id));
	})();

	const rationaleRequired = Dtos.Decisioning.requiresRationale(importance);
	const rationaleMissing = rationaleRequired && detail.trim() === "";
	const resolvedGroup = resolveGroup(newFactor, factorGroups);
	const newFactorValid =
		newFactor.name.trim() !== "" &&
		(resolvedGroup === NEW_GROUP
			? Dtos.Decisioning.FactorGroup.Create.Request.schema.safeParse({
					lens: newFactor.lens,
					name: newFactor.newGroupName,
				}).success
			: true);

	const missingFactor = !editing && (creating ? false : factorId === "");
	const missingNewFactor = !editing && creating && !newFactorValid;
	const blocked = missingFactor || missingNewFactor || rationaleMissing || valueInvalid;

	const status = (() => {
		if (missingFactor) return "Choose a factor to continue.";
		if (missingNewFactor) return "Name and group are required.";
		if (rationaleMissing) return "Override needs a rationale.";
		if (valueInvalid) return "Enter a value, or use account data.";
		if (submitError) return submitError;
		const label = PRO_CON_IMPORTANCE_LABELS[importance];
		if (!editing) return undefined;
		return importance === proCon.importance
			? "Saves your changes."
			: `Moves to the end of ${label}.`;
	})();
	const statusTone = rationaleMissing || submitError ? "danger" : "default";

	const resolveFactor = async (): Promise<Factor | undefined> => {
		if (!creating) return selectedFactor;
		let groupId = resolvedGroup === NEW_GROUP ? undefined : resolvedGroup.id;
		if (groupId === undefined) {
			try {
				const group = await createFactorGroupAsync({
					lens: newFactor.lens,
					name: newFactor.newGroupName,
				});
				groupId = group.id;
				setNewFactor((draft) => ({ ...draft, groupId: group.id }));
			} catch (error) {
				setGroupError(
					groupCreateErrorMessage(error, newFactor.newGroupName.trim(), newFactor.lens),
				);
				return undefined;
			}
		}
		try {
			return await createFactorAsync({
				name: newFactor.name,
				groupId,
				valueType: "text",
			});
		} catch (error) {
			const groupName =
				resolvedGroup === NEW_GROUP ? newFactor.newGroupName.trim() : resolvedGroup.name;
			setSubmitError(factorCreateErrorMessage(error, newFactor.name.trim(), groupName));
			return undefined;
		}
	};

	const changeNewFactor = (next: NewFactorDraft) => {
		if (
			next.lens !== newFactor.lens ||
			next.groupId !== newFactor.groupId ||
			next.newGroupName !== newFactor.newGroupName
		) {
			setGroupError(undefined);
		}
		if (next.name !== newFactor.name) setSubmitError(undefined);
		setNewFactor(next);
	};

	const observedValueChange = (existing: ProCon): Dtos.Decisioning.ProCon.Update.Request => {
		if (!overriding) return hadOverride ? { observedValue: null } : {};
		return hadOverride && parsedValue === existing.observedValue
			? {}
			: { observedValue: parsedValue };
	};

	const saveEdit = (existing: ProCon) => {
		const trimmed = detail.trim();
		const changes: Dtos.Decisioning.ProCon.Update.Request = {
			...(importance === existing.importance ? {} : { importance }),
			...(trimmed === (existing.detail ?? "") ? {} : { detail: trimmed === "" ? null : trimmed }),
			...observedValueChange(existing),
		};
		closeSheet();
		jumpTo({ tab: "factors", id: existing.id });
		if (Object.keys(changes).length === 0) return;
		savedDraft.current = { importance, detail, overriding, valueDraft };
		toastOnSave(updateProConAsync({ id: existing.id, changes }), `Updated ${existing.factor.name}`);
	};

	const saveNew = async () => {
		setSubmitting(true);
		setGroupError(undefined);
		setSubmitError(undefined);
		const factor = await resolveFactor();
		if (!factor) {
			setSubmitting(false);
			return;
		}
		const trimmed = detail.trim();
		const saved = createProConAsync({
			kind,
			factorId: factor.id,
			importance,
			detail: trimmed === "" ? null : trimmed,
			...(overriding ? { observedValue: parsedValue } : {}),
			factor,
		});
		setTab("factors");
		closeSheet();
		toastOnSave(saved, `Added ${factor.name} to ${KIND_NOUN[kind]}`);
	};

	const handlePrimary = () => {
		if (blocked || submitting) return;
		if (proCon) saveEdit(proCon);
		else void saveNew();
	};

	const title = editing ? "Edit factor" : kind === "pro" ? "Add a pro" : "Add a con";
	const primaryLabel = editing ? "Save" : kind === "pro" ? "Add pro" : "Add con";

	return (
		<PanelSheet
			title={title}
			status={status}
			statusTone={statusTone}
			onCancel={closeSheet}
			dirty={dirty}
			primaryLabel={primaryLabel}
			onPrimary={handlePrimary}
			primaryDisabled={blocked}
			primaryLoading={submitting}
			testId="factor-sheet"
		>
			<div className="flex flex-col gap-5">
				{proCon ? (
					<ReadOnlyFactor proCon={proCon} />
				) : (
					<>
						<SegmentedControls<Kind>
							aria-label="Side"
							value={kind}
							onChange={setKind}
							className="flex w-full"
						>
							{KINDS.map((side) => (
								<SegmentedControls.Segment
									key={side}
									value={side}
									className="flex-1 gap-1.5"
									data-testid={`factor-sheet-side-${side}`}
								>
									<span
										aria-hidden
										className={cn("size-1.5 rounded-full", SIDE_DOT_CLASSES[side])}
									/>
									{KIND_LABELS[side]}
								</SegmentedControls.Segment>
							))}
						</SegmentedControls>
						{creating ? (
							<NewFactorBox
								draft={newFactor}
								onChange={changeNewFactor}
								groups={factorGroups}
								groupError={groupError}
								onBack={() => {
									setCreating(false);
									setGroupError(undefined);
								}}
							/>
						) : (
							<PanelField
								label="Factor"
								required
								helper={selectedFactor ? groupLabel(selectedFactor) : undefined}
							>
								{({ id, labelId }) => (
									<SearchableDropdownInput
										id={id}
										aria-labelledby={`${labelId} ${id}`}
										compact
										value={factorId}
										options={options}
										placeholder="Choose a factor"
										searchPlaceholder="Search factors, e.g. FSR, backlog"
										searchPlacement="popover"
										emptyMessage="No factors match."
										onChange={setFactorId}
										footerAction={{
											label: (query) => {
												const existing = findFactorNamed(factors, query);
												if (existing) return `"${existing.name}" already exists`;
												return query.trim()
													? `Create "${query.trim()}" as a new factor`
													: "Create a new factor";
											},
											onSelect: (query) => {
												const existing = findFactorNamed(factors, query);
												if (existing) {
													setFactorId(existing.id);
													return;
												}
												setNewFactor({ ...EMPTY_NEW_FACTOR, name: query.trim() });
												setFactorId("");
												setCreating(true);
											},
										}}
									/>
								)}
							</PanelField>
						)}
					</>
				)}
				{valueType && (
					<ObservedValueField
						valueType={valueType}
						preview={preview}
						overriding={overriding}
						onOverridingChange={setOverriding}
						draft={valueDraft}
						onDraftChange={setValueDraft}
						invalid={valueInvalid}
					/>
				)}
				<PanelField label="Importance" required>
					<ImportancePicker value={importance} onChange={setImportance} side={kind} />
				</PanelField>
				<PanelField label="Rationale" required={rationaleRequired}>
					{({ id }) => (
						<TextAreaInput
							id={id}
							placeholder="Why it matters for this account"
							value={detail}
							invalid={rationaleMissing}
							data-testid="factor-sheet-rationale"
							className="min-h-[76px]"
							onChange={(event) => setDetail(event.target.value ?? "")}
						/>
					)}
				</PanelField>
			</div>
		</PanelSheet>
	);
};
